import { useState, useEffect, useRef } from 'react'
import API from '../../services/apiService'
import { downloadUpdate, showDownloadError, showDownloadSuccess } from '../../services/downloadService'
import { Skeleton } from '../Skeleton/Skeleton'
import { useConfirm } from '../ConfirmModal/ConfirmModal'
import ShareModal from './ShareModal'
import './BotManager.css'

// ── SVG Icons (matching site's medieval style) ──────────────────────────

const BotIcon = () => (
  <svg viewBox="0 0 24 24" width="40" height="40">
    <defs>
      <linearGradient id="botGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#e6c84a" />
        <stop offset="40%" stopColor="#8b7355" />
        <stop offset="100%" stopColor="#5c4a3a" />
      </linearGradient>
      <linearGradient id="botBody" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#6b5949" />
        <stop offset="100%" stopColor="#4a3829" />
      </linearGradient>
      <linearGradient id="botEyes" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#22c55e" />
        <stop offset="100%" stopColor="#4ade80" />
      </linearGradient>
      <filter id="botGlow">
        <feGaussianBlur stdDeviation="1.2" />
        <feComponentTransfer>
          <feFuncA type="linear" slope="1.5"/>
        </feComponentTransfer>
      </filter>
    </defs>
    
    <rect x="5" y="8" width="14" height="11" rx="2.5" fill="url(#botBody)" stroke="#e6c84a" strokeWidth="0.8" />
    <rect x="5.5" y="8.5" width="13" height="10" rx="2" fill="none" stroke="rgba(230, 200, 74, 0.3)" strokeWidth="0.5" />
    
    <rect x="2" y="11" width="3" height="6" rx="1.5" fill="url(#botBody)" stroke="#8b7355" strokeWidth="0.6" />
    <rect x="19" y="11" width="3" height="6" rx="1.5" fill="url(#botBody)" stroke="#8b7355" strokeWidth="0.6" />
    <circle cx="3.5" cy="17.5" r="1.2" fill="#e6c84a" stroke="#8b7355" strokeWidth="0.5" />
    <circle cx="20.5" cy="17.5" r="1.2" fill="#e6c84a" stroke="#8b7355" strokeWidth="0.5" />
    
    <ellipse cx="9" cy="12.5" rx="1.8" ry="2" fill="url(#botEyes)" filter="url(#botGlow)" opacity="0.9" />
    <ellipse cx="15" cy="12.5" rx="1.8" ry="2" fill="url(#botEyes)" filter="url(#botGlow)" opacity="0.9" />
    <circle cx="9.5" cy="12" r="0.6" fill="#dcfce7" opacity="0.9" />
    <circle cx="15.5" cy="12" r="0.6" fill="#dcfce7" opacity="0.9" />
    
    <rect x="9.5" y="15.5" width="5" height="2.5" rx="0.5" fill="rgba(230, 200, 74, 0.25)" stroke="#e6c84a" strokeWidth="0.5" />
    <line x1="10.5" y1="16.2" x2="13.5" y2="16.2" stroke="#e6c84a" strokeWidth="0.3" opacity="0.7" />
    <line x1="10.5" y1="17" x2="12" y2="17" stroke="#e6c84a" strokeWidth="0.3" opacity="0.7" />
    
    <rect x="10.5" y="3.5" width="3" height="4.5" rx="1.5" fill="url(#botGrad)" stroke="#8b7355" strokeWidth="0.7" />
    <circle cx="12" cy="2.5" r="1.3" fill="#e6c84a" stroke="#8b7355" strokeWidth="0.6" />
    <circle cx="12" cy="2.5" r="0.5" fill="#22c55e" filter="url(#botGlow)" />
    
    <rect x="7.5" y="19" width="2.5" height="3" rx="1" fill="url(#botBody)" stroke="#8b7355" strokeWidth="0.6" />
    <rect x="14" y="19" width="2.5" height="3" rx="1" fill="url(#botBody)" stroke="#8b7355" strokeWidth="0.6" />
    <ellipse cx="8.75" cy="22.5" rx="1.5" ry="0.8" fill="#e6c84a" opacity="0.6" />
    <ellipse cx="15.25" cy="22.5" rx="1.5" ry="0.8" fill="#e6c84a" opacity="0.6" />
  </svg>
)

const UploadIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16">
    <defs>
      <linearGradient id="uploadGr" x1="0%" y1="100%" x2="0%" y2="0%">
        <stop offset="0%" stopColor="#86efac"/>
        <stop offset="100%" stopColor="#22c55e"/>
      </linearGradient>
    </defs>
    <path d="M12 16V4M12 4L8 8M12 4L16 8" stroke="url(#uploadGr)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    <path d="M4 14v4a2 2 0 002 2h12a2 2 0 002-2v-4" stroke="url(#uploadGr)" strokeWidth="2" strokeLinecap="round" fill="none"/>
  </svg>
)

const VersionIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16">
    <defs>
      <linearGradient id="verGr" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#a5b4fc"/>
        <stop offset="100%" stopColor="#6366f1"/>
      </linearGradient>
    </defs>
    <path d="M12 2L2 7l10 5 10-5-10-5z" fill="url(#verGr)" opacity="0.6"/>
    <path d="M2 17l10 5 10-5" stroke="url(#verGr)" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M2 12l10 5 10-5" stroke="url(#verGr)" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

const ChangelogIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16">
    <defs>
      <linearGradient id="logGr" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#c4b5fd"/>
        <stop offset="100%" stopColor="#8b5cf6"/>
      </linearGradient>
    </defs>
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" fill="url(#logGr)" stroke="#5b21b6" strokeWidth="1"/>
    <path d="M14 2v6h6" fill="none" stroke="#5b21b6" strokeWidth="1"/>
    <line x1="8" y1="13" x2="16" y2="13" stroke="#1e1b4b" strokeWidth="1.2" opacity="0.6"/>
    <line x1="8" y1="17" x2="13" y2="17" stroke="#1e1b4b" strokeWidth="1.2" opacity="0.6"/>
  </svg>
)

const UpdateIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16">
    <defs>
      <linearGradient id="updGr" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#67e8f9"/>
        <stop offset="100%" stopColor="#06b6d4"/>
      </linearGradient>
    </defs>
    <path d="M21 12a9 9 0 11-2.6-6.3" stroke="url(#updGr)" strokeWidth="2" strokeLinecap="round" fill="none"/>
    <path d="M21 3v6h-6" stroke="url(#updGr)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
  </svg>
)

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14">
    <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z"
      stroke="#f87171" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

const DownloadSmIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14">
    <path d="M12 4v12m0 0l-4-4m4 4l4-4M4 18h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
  </svg>
)

const RefreshSmIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14">
    <path d="M21 12a9 9 0 11-2.6-6.3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none"/>
    <path d="M21 3v6h-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
  </svg>
)

const StatusIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16">
    <defs>
      <linearGradient id="statusGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#34d399" />
        <stop offset="50%" stopColor="#22c55e" />
        <stop offset="100%" stopColor="#16a34a" />
      </linearGradient>
      <radialGradient id="statusGlow" cx="50%" cy="50%">
        <stop offset="0%" stopColor="rgba(34,197,94,0.4)"/>
        <stop offset="100%" stopColor="rgba(34,197,94,0)"/>
      </radialGradient>
      <filter id="statusShadow">
        <feDropShadow dx="0" dy="1" stdDeviation="1.5" floodOpacity="0.4"/>
      </filter>
    </defs>
    <circle cx="12" cy="12" r="10" fill="url(#statusGrad)" stroke="#065f46" strokeWidth="1.5" filter="url(#statusShadow)"/>
    <circle cx="12" cy="12" r="10" fill="url(#statusGlow)" opacity="0.6"/>
    <path d="M7 12l3 3 7-7" stroke="#dcfce7" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    <circle cx="12" cy="12" r="7" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="0.8"/>
  </svg>
)

// ── Component ─────────────────────────────────────────────────────────

const BotManager = () => {
  // Sub-tab navigation
  const [activeSubTab, setActiveSubTab] = useState('overview')

  // Data states
  const [latestVersion, setLatestVersion] = useState(null)
  const [allVersions, setAllVersions] = useState([])
  const [changelog, setChangelog] = useState([])
  const [shareEntry, setShareEntry] = useState(null)
  const [loading, setLoading] = useState(true)

  // Messages
  const [success, setSuccess] = useState(null)
  const [error, setError] = useState(null)

  // New Version Form
  const [showNewVersionForm, setShowNewVersionForm] = useState(false)
  const [newVersionData, setNewVersionData] = useState({
    version: '',
    changelog: '',
    mandatory: false,
    files: []
  })
  const [uploading, setUploading] = useState(false)
  const filesInputRef = useRef(null)

  // Installer Management (separate from versions)
  const [installerFile, setInstallerFile] = useState(null)
  const [uploadingInstaller, setUploadingInstaller] = useState(false)
  const [installerDragOver, setInstallerDragOver] = useState(false)
  const [filesDragOver, setFilesDragOver] = useState(false)
  const installerInputRef = useRef(null)

  // Edit changelog
  const [editingVersionId, setEditingVersionId] = useState(null)
  const [editChangelogText, setEditChangelogText] = useState('')
  const [downloadingVersion, setDownloadingVersion] = useState(null)

  const confirm = useConfirm()

  // ── Data loading ──────────────────────────────────────────────────

  const loadAll = async () => {
    setLoading(true)
    try {
      const [latestRes, versionsRes, changelogRes] = await Promise.all([
        API.Bot.getLatest(),
        API.Bot.getAllVersions(),
        API.Bot.getChangelog()
      ])

      if (latestRes.success) {
        setLatestVersion(latestRes.data)
      }
      if (versionsRes.success) {
        setAllVersions(versionsRes.data?.versions || [])
      }
      if (changelogRes.success) {
        setChangelog(changelogRes.data?.changelog || [])
      }
    } catch (err) {
      console.error('Failed to load bot data:', err)
      setError('Failed to load bot data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { loadAll() }, [])

  // Auto-clear messages
  useEffect(() => {
    if (success) {
      const t = setTimeout(() => setSuccess(null), 4000)
      return () => clearTimeout(t)
    }
  }, [success])

  // ── Handlers ──────────────────────────────────────────────────────

  const handleCreateVersion = async () => {
    if (!newVersionData.version || !newVersionData.changelog.trim()) {
      setError('Version and changelog are required')
      return
    }
    if (newVersionData.files.length === 0) {
      setError('Update files are required')
      return
    }

    setUploading(true)
    setError(null)
    try {
      const changelog = newVersionData.changelog.split('\n').filter(l => l.trim())
      const res = await API.Bot.createVersion({
        version: newVersionData.version,
        changelog: changelog,
        mandatory: newVersionData.mandatory,

        files: newVersionData.files
      })

      if (res.success) {
        setSuccess(`Version ${newVersionData.version} created successfully`)
        setNewVersionData({
          version: '',
          changelog: '',
          mandatory: false,
          files: []
        })
        setShowNewVersionForm(false)
        await loadAll()
      } else {
        setError(res.error?.message || res.message || 'Failed to create version')
      }
    } catch (err) {
      setError('Failed to create version: ' + (err.message || 'Unknown error'))
    } finally {
      setUploading(false)
    }
  }

  const handlePublishVersion = async (versionId, versionName) => {
    if (!await confirm(`Publish version ${versionName}? It will become publicly available.`)) return
    setError(null)
    try {
      const res = await API.Bot.publishVersion(versionId)
      if (res.success) {
        setSuccess(`Version ${versionName} published`)
        await loadAll()
      } else {
        setError(res.error?.message || res.message || 'Failed to publish')
      }
    } catch (err) {
      setError('Failed to publish version')
    }
  }

  const handleUnpublishVersion = async (versionId, versionName) => {
    if (!await confirm(`Unpublish version ${versionName}? It will be removed from public access.`)) return
    setError(null)
    try {
      const res = await API.Bot.unpublishVersion(versionId)
      if (res.success) {
        setSuccess(`Version ${versionName} unpublished`)
        await loadAll()
      } else {
        setError(res.error?.message || res.message || 'Failed to unpublish')
      }
    } catch (err) {
      setError('Failed to unpublish version')
    }
  }

  const handleDeleteVersion = async (versionId, versionName) => {
    if (!await confirm(`Delete version ${versionName}? This cannot be undone and will delete all associated files.`)) return
    setError(null)
    try {
      const res = await API.Bot.deleteVersion(versionId)
      if (res.success) {
        setSuccess(`Version ${versionName} deleted`)
        await loadAll()
      } else {
        setError(res.error?.message || res.message || 'Failed to delete')
      }
    } catch (err) {
      setError('Failed to delete version')
    }
  }

  const handleDownloadUpdate = async (e, version) => {
    e.preventDefault()
    
    if (downloadingVersion) return
    
    setDownloadingVersion(version)
    
    const result = await downloadUpdate(version)
    
    if (result.success) {
      showDownloadSuccess(result.message)
    } else {
      showDownloadError(result.error)
    }
    
    setDownloadingVersion(null)
  }

  const handleEditChangelog = async (versionId, versionName) => {
    if (!editChangelogText.trim()) return
    const changes = editChangelogText.split('\n').filter(l => l.trim())
    setError(null)
    try {
      const res = await API.Bot.editChangelog(versionId, changes)
      if (res.success) {
        setSuccess(`Changelog updated for v${versionName}`)
        setEditingVersionId(null)
        setEditChangelogText('')
        await loadAll()
      } else {
        setError(res.error?.message || res.message || 'Failed to update changelog')
      }
    } catch (err) {
      setError('Failed to update changelog')
    }
  }

  const startEditingChangelog = (version) => {
    setEditingVersionId(version.id)
    setEditChangelogText((version.changelog || []).join('\n'))
  }

  const handleUploadInstaller = async () => {
    if (!installerFile) {
      setError('Please select an installer file (.exe)')
      return
    }

    setUploadingInstaller(true)
    setError(null)

    try {
      const res = await API.Bot.uploadInstaller(installerFile)
      if (res.success) {
        setSuccess(`Installer uploaded successfully: ${res.data.sizeFormatted}`)
        setInstallerFile(null)
        if (installerInputRef.current) installerInputRef.current.value = ''
        await loadAll()
      } else {
        setError(res.error?.message || res.message || 'Failed to upload installer')
      }
    } catch (err) {
      setError('Failed to upload installer: ' + (err.message || 'Unknown error'))
    } finally {
      setUploadingInstaller(false)
    }
  }

  // ── Drag & Drop Handlers ─────────────────────────────────────────

  const handleDragOver = (e, setter) => {
    e.preventDefault()
    e.stopPropagation()
    setter(true)
  }

  const handleDragLeave = (e, setter) => {
    e.preventDefault()
    e.stopPropagation()
    setter(false)
  }

  const handleInstallerDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setInstallerDragOver(false)
    const file = e.dataTransfer.files?.[0]
    if (file) {
      if (file.name.endsWith('.exe')) {
        setInstallerFile(file)
      } else {
        setError('Only .exe files are allowed')
      }
    }
  }

  const handleVersionFilesDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setFilesDragOver(false)
    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) {
      setNewVersionData(d => ({ ...d, files }))
    }
  }

  // ── Helpers ───────────────────────────────────────────────────────

  const formatDate = (d) => {
    if (!d) return 'N/A'
    return new Date(d).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    })
  }

  const formatSize = (bytes) => {
    if (!bytes) return 'N/A'
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / 1048576).toFixed(2) + ' MB'
  }

  // ── Subtabs definition ────────────────────────────────────────────

  const subtabs = [
    { key: 'overview', label: 'Overview', icon: <BotIcon /> },
    { key: 'installer', label: 'Installer', icon: <DownloadSmIcon /> },
    { key: 'versions', label: 'Versions', icon: <VersionIcon /> },
    { key: 'changelog', label: 'Changelog', icon: <ChangelogIcon /> },
  ]

  // ── Loading state ─────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="bot-manager">
        <Skeleton width="100%" height="80px" borderRadius="12px" />
        <Skeleton width="100%" height="40px" borderRadius="8px" />
        <Skeleton width="100%" height="120px" borderRadius="12px" />
      </div>
    )
  }

  // ── Render ────────────────────────────────────────────────────────

  return (
    <div className="bot-manager">
      {/* Messages */}
      {success && (
        <div className="bot-manager__message bot-manager__message--success">
          ✅ {success}
          <button className="bot-manager__message-close" onClick={() => setSuccess(null)}>×</button>
        </div>
      )}
      {error && (
        <div className="bot-manager__message bot-manager__message--error">
          ⚠️ {typeof error === 'object' ? error?.message || 'Error' : error}
          <button className="bot-manager__message-close" onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Subtab Navigation */}
      <div className="bot-manager__subtabs">
        {subtabs.map(t => (
          <button
            key={t.key}
            className={`bot-manager__subtab ${activeSubTab === t.key ? 'bot-manager__subtab--active' : ''}`}
            onClick={() => setActiveSubTab(t.key)}
          >
            {t.icon}
            <span>{t.label}</span>
          </button>
        ))}
      </div>

      {/* ═══════ OVERVIEW ═══════ */}
      {activeSubTab === 'overview' && (
        <div className="bot-manager__section">
          <div className="bot-manager__section-header">
            <h5><StatusIcon /> Unity-BOT Status</h5>
            <button className="bot-manager__btn bot-manager__btn--primary bot-manager__btn--small" onClick={loadAll}>
              <RefreshSmIcon /> Refresh
            </button>
          </div>

          <div className="bot-manager__status-card">
            <div className="bot-manager__status-icon">
              <img 
                src="/assets/logo-128x128.svg" 
                alt="Unity-BOT" 
                className="bot-manager__status-logo"
              />
            </div>
            <div className="bot-manager__status-info">
              <div className="bot-manager__status-version">
                Unity-BOT v{latestVersion?.version || '—'}
              </div>
            </div>
          </div>

          <div className="bot-manager__info-grid">
            <div className="bot-manager__info-item">
              <span className="bot-manager__info-label">Latest Version</span>
              <span className="bot-manager__info-value">{latestVersion?.version || 'No version published'}</span>
            </div>
            <div className="bot-manager__info-item">
              <span className="bot-manager__info-label">Installer Size</span>
              <span className="bot-manager__info-value">{latestVersion?.installerSizeFormatted || 'N/A'}</span>
            </div>
            <div className="bot-manager__info-item">
              <span className="bot-manager__info-label">Published</span>
              <span className="bot-manager__info-value">{formatDate(latestVersion?.publishedAt)}</span>
            </div>
            <div className="bot-manager__info-item">
              <span className="bot-manager__info-label">Total Versions</span>
              <span className="bot-manager__info-value">{allVersions.length}</span>
            </div>
          </div>

          {latestVersion && (
            <>
              <div className="bot-manager__divider" />
              <div className="bot-manager__section-header">
                <h6>Latest Changelog</h6>
              </div>
              <ul className="bot-manager__changelog-changes">
                {(latestVersion.changelog || []).map((change, i) => (
                  <li key={i}>{change}</li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {/* ═══════ INSTALLER ═══════ */}
      {activeSubTab === 'installer' && (
        <div className="bot-manager__section">
          <div className="bot-manager__section-header">
            <h5><DownloadSmIcon /> Installer Management</h5>
          </div>

          <div className="bot-manager__info-card">
            <p className="bot-manager__info-text">
              The installer is <strong>independent from versions</strong>. Upload a new .exe file to replace the current installer. 
              This will affect all new downloads from the public download page.
            </p>
          </div>

          {latestVersion && (
            <div className="bot-manager__info-grid">
              <div className="bot-manager__info-item">
                <span className="bot-manager__info-label">Current Installer</span>
                <span className="bot-manager__info-value">
                  {latestVersion.hasInstaller ? 'Unity-BOT-installer.exe' : 'Not uploaded'}
                </span>
              </div>
              <div className="bot-manager__info-item">
                <span className="bot-manager__info-label">File Size</span>
                <span className="bot-manager__info-value">{latestVersion?.installerSizeFormatted || 'N/A'}</span>
              </div>
              <div className="bot-manager__info-item">
                <span className="bot-manager__info-label">Last Updated</span>
                <span className="bot-manager__info-value">{formatDate(latestVersion?.installerUpdatedAt)}</span>
              </div>
            </div>
          )}

          <div className="bot-manager__divider" />

          {/* Upload Form */}
          <div className="bot-manager__section-header">
            <h6>Upload New Installer</h6>
          </div>

          <div className="bot-manager__form">
              <div className="bot-manager__form-group">
                <label className="bot-manager__form-label">Installer File (.exe)</label>
                <div 
                  className={`bot-manager__upload-zone ${installerFile ? 'bot-manager__upload-zone--active' : ''} ${installerDragOver ? 'bot-manager__upload-zone--dragover' : ''}`}
                  onClick={() => installerInputRef.current?.click()}
                  onDragOver={(e) => handleDragOver(e, setInstallerDragOver)}
                  onDragLeave={(e) => handleDragLeave(e, setInstallerDragOver)}
                  onDrop={handleInstallerDrop}
                >
                  {installerFile ? (
                    <div className="bot-manager__upload-info">
                      📦 {installerFile.name} ({formatSize(installerFile.size)})
                      <button 
                        className="bot-manager__upload-clear"
                        onClick={(e) => {
                          e.stopPropagation()
                          setInstallerFile(null)
                          if (installerInputRef.current) installerInputRef.current.value = ''
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <>
                      <UploadIcon />
                      <span className="bot-manager__upload-text">Click to select or drag & drop installer file</span>
                      <span className="bot-manager__upload-hint">Only .exe files (max 500 MB)</span>
                    </>
                  )}
                </div>
                <input
                  ref={installerInputRef}
                  type="file"
                  accept=".exe"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const f = e.target.files?.[0]
                    if (f) setInstallerFile(f)
                  }}
                />
              </div>

            <button
              className="bot-manager__btn bot-manager__btn--primary"
              onClick={handleUploadInstaller}
              disabled={!installerFile || uploadingInstaller}
            >
              {uploadingInstaller ? 'Uploading...' : 'Upload Installer'}
            </button>
          </div>
        </div>
      )}

      {/* ═══════ VERSIONS ═══════ */}
      {activeSubTab === 'versions' && (
        <div className="bot-manager__section">
          <div className="bot-manager__section-header">
            <h5><VersionIcon /> Version Management</h5>
            <button
              className="bot-manager__btn bot-manager__btn--primary bot-manager__btn--small"
              onClick={() => setShowNewVersionForm(!showNewVersionForm)}
            >
              {showNewVersionForm ? '✕ Cancel' : '+ New Version'}
            </button>
          </div>

          {showNewVersionForm && (
            <div className="bot-manager__changelog-form">
              <div className="bot-manager__form-group">
                <label className="bot-manager__form-label">Version Number *</label>
                <input
                  type="text"
                  className="bot-manager__input"
                  value={newVersionData.version}
                  onChange={e => setNewVersionData(d => ({ ...d, version: e.target.value }))}
                  placeholder="e.g. 1.2.7.8"
                />
              </div>

              <div className="bot-manager__form-group">
                <label className="bot-manager__form-label">Changelog * (one change per line)</label>
                <textarea
                  className="bot-manager__input bot-manager__textarea"
                  value={newVersionData.changelog}
                  onChange={e => setNewVersionData(d => ({ ...d, changelog: e.target.value }))}
                  placeholder={"Fixed crash on inventory open\nAdded new map support\nPerformance improvements"}
                  rows={4}
                />
              </div>

              <div className="bot-manager__form-group">
                <label className="bot-manager__form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={newVersionData.mandatory}
                    onChange={e => setNewVersionData(d => ({ ...d, mandatory: e.target.checked }))}
                  />
                  Mandatory Update
                </label>
              </div>

              <div className="bot-manager__form-group">
                <label className="bot-manager__form-label">Update Files * (will be zipped)</label>
                <div
                  className={`bot-manager__upload-zone ${newVersionData.files.length ? 'bot-manager__upload-zone--active' : ''} ${filesDragOver ? 'bot-manager__upload-zone--dragover' : ''}`}
                  onClick={() => {
                    console.log('Upload zone clicked, opening file picker...')
                    filesInputRef.current?.click()
                  }}
                  onDragOver={(e) => handleDragOver(e, setFilesDragOver)}
                  onDragLeave={(e) => handleDragLeave(e, setFilesDragOver)}
                  onDrop={handleVersionFilesDrop}
                >
                  {newVersionData.files.length > 0 ? (
                    <div className="bot-manager__upload-file" style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <span>📁 {newVersionData.files.length} file(s) selected</span>
                        <button 
                          className="bot-manager__upload-remove" 
                          onClick={e => { 
                            e.stopPropagation(); 
                            setNewVersionData(d => ({ ...d, files: [] }))
                          }}
                        >×</button>
                      </div>
                      <div style={{ fontSize: '11px', color: '#94a3b8', maxHeight: '80px', overflow: 'auto' }}>
                        {newVersionData.files.map((f, i) => (
                          <div key={i}>• {f.name} ({formatSize(f.size)})</div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <>
                      <UploadIcon />
                      <span className="bot-manager__upload-text">Click to select or drag & drop update files</span>
                      <span className="bot-manager__upload-hint">Optional • DLLs, configs, etc • Max 50 files</span>
                    </>
                  )}
                </div>
                <input
                  ref={filesInputRef}
                  type="file"
                  multiple
                  className="bot-manager__file-input"
                  onChange={e => {
                    const files = Array.from(e.target.files)
                    console.log('Files selected:', files.length, files.map(f => f.name))
                    setNewVersionData(d => ({ ...d, files: files }))
                    e.target.value = ''
                  }}
                />
              </div>

              <div className="bot-manager__changelog-form-actions">
                <button
                  className="bot-manager__btn bot-manager__btn--success"
                  onClick={handleCreateVersion}
                  disabled={uploading || !newVersionData.version || !newVersionData.changelog.trim()}
                >
                  {uploading ? '⏳ Creating...' : '✅ Create Version'}
                </button>
              </div>
            </div>
          )}

          <div className="bot-manager__divider" />

          {/* Existing versions */}
          <div className="bot-manager__updates-list">
            {allVersions.length === 0 ? (
              <div className="bot-manager__empty">No versions created yet</div>
            ) : (
              allVersions.map(v => (
                <div key={v.id} className="bot-manager__update-item" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <div className="bot-manager__update-info">
                      <span className="bot-manager__update-version">
                        v{v.version}
                        {v.published && <span style={{ marginLeft: '8px', padding: '2px 8px', background: '#22c55e', color: 'white', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold' }}>PUBLISHED</span>}
                        {v.mandatory && <span style={{ marginLeft: '8px', padding: '2px 8px', background: '#ef4444', color: 'white', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold' }}>MANDATORY</span>}
                      </span>
                      <span className="bot-manager__update-meta">
                        {v.hasUpdate ? `Update ZIP: ${v.updateSizeFormatted || 'N/A'}` : 'No update files'}
                        {' • '}
                        Created: {formatDate(v.createdAt)}
                        {v.publishedAt && ` • Published: ${formatDate(v.publishedAt)}`}
                      </span>
                    </div>
                    <div className="bot-manager__update-actions">
                      {v.published ? (
                        <button
                          className="bot-manager__btn bot-manager__btn--outline bot-manager__btn--small"
                          onClick={() => handleUnpublishVersion(v.id, v.version)}
                          title="Unpublish"
                        >
                          Hide
                        </button>
                      ) : (
                        <button
                          className="bot-manager__btn bot-manager__btn--success bot-manager__btn--small"
                          onClick={() => handlePublishVersion(v.id, v.version)}
                          title="Publish"
                        >
                          Publish
                        </button>
                      )}
                      <button
                        className="bot-manager__btn bot-manager__btn--outline bot-manager__btn--small"
                        onClick={() => startEditingChangelog(v)}
                        title="Edit Changelog"
                      >
                        Edit
                      </button>
                      {v.hasUpdate && (
                        <button
                          onClick={(e) => handleDownloadUpdate(e, v.version)}
                          disabled={downloadingVersion === v.version}
                          className="bot-manager__btn bot-manager__btn--outline bot-manager__btn--small"
                          title={downloadingVersion === v.version ? 'Downloading...' : 'Download ZIP'}
                        >
                          <DownloadSmIcon />
                        </button>
                      )}
                      <button
                        className="bot-manager__btn bot-manager__btn--danger bot-manager__btn--small"
                        onClick={() => handleDeleteVersion(v.id, v.version)}
                        title="Delete"
                      >
                        <TrashIcon />
                      </button>
                    </div>
                  </div>

                  {editingVersionId === v.id ? (
                    <div className="bot-manager__changelog-form" style={{ marginTop: '12px' }}>
                      <textarea
                        className="bot-manager__input bot-manager__textarea"
                        value={editChangelogText}
                        onChange={e => setEditChangelogText(e.target.value)}
                        rows={4}
                      />
                      <div className="bot-manager__changelog-form-actions">
                        <button
                          className="bot-manager__btn bot-manager__btn--success bot-manager__btn--small"
                          onClick={() => handleEditChangelog(v.id, v.version)}
                        >
                          Save
                        </button>
                        <button
                          className="bot-manager__btn bot-manager__btn--outline bot-manager__btn--small"
                          onClick={() => { setEditingVersionId(null); setEditChangelogText('') }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <ul className="bot-manager__changelog-changes" style={{ marginTop: '8px' }}>
                      {(v.changelog || []).map((change, i) => (
                        <li key={i}>{change}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* ═══════ CHANGELOG ═══════ */}
      {activeSubTab === 'changelog' && (
        <div className="bot-manager__section">
          <div className="bot-manager__section-header">
            <h5><ChangelogIcon /> Public Changelog</h5>
            <button className="bot-manager__btn bot-manager__btn--primary bot-manager__btn--small" onClick={loadAll}>
              <RefreshSmIcon /> Refresh
            </button>
          </div>

          <div className="bot-manager__changelog-list">
            {changelog.length === 0 ? (
              <div className="bot-manager__empty">No published versions yet</div>
            ) : (
              changelog.map(entry => (
                <div key={entry.version} className="bot-manager__changelog-item">
                  <div className="bot-manager__changelog-header">
                    <span className="bot-manager__changelog-version">
                      v{entry.version}
                      {entry.mandatory && <span style={{ marginLeft: '8px', padding: '2px 8px', background: '#ef4444', color: 'white', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold' }}>MANDATORY</span>}
                    </span>
                    <div className="bot-manager__changelog-header-right">
                      <button
                        className="bot-manager__share-btn"
                        onClick={() => setShareEntry(entry)}
                        title="Share this update"
                      >
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="18" cy="5" r="3" />
                          <circle cx="6" cy="12" r="3" />
                          <circle cx="18" cy="19" r="3" />
                          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                          <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                        </svg>
                      </button>
                      <span className="bot-manager__changelog-date">{formatDate(entry.date)}</span>
                    </div>
                  </div>
                  <ul className="bot-manager__changelog-changes">
                    {(entry.changes || []).map((change, i) => (
                      <li key={i}>{change}</li>
                    ))}
                  </ul>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {shareEntry && (
        <ShareModal entry={shareEntry} onClose={() => setShareEntry(null)} />
      )}
    </div>
  )
}

export default BotManager
